import { useState, useEffect } from 'react'
import { Alert, Button, Card, Container, Form, Table } from 'react-bootstrap'
import {
  fetchUsers,
  createDivision,
  deleteDivision,
  fetchDivisions,
  fetchSubdivisions,
  fetchBusinessEntities,
  updateDivision,
  createSubdivision,
  updateSubdivision,
  createBusinessEntity,
  updateBusinessEntity,
  deleteBusinessEntity,
  type AppUserDto,
  type DivisionDto,
  type SubdivisionDto,
  type BusinessEntityDto,
} from '../services/api'
import { useAuth } from '../context/AuthContext'

export default function DivisionManagement() {
  const { isAuthenticated, user: currentUser } = useAuth()
  const [users, setUsers] = useState<AppUserDto[]>([])
  const [divisions, setDivisions] = useState<DivisionDto[]>([])
  const [subdivisions, setSubdivisions] = useState<SubdivisionDto[]>([])
  const [businessEntities, setBusinessEntities] = useState<BusinessEntityDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [divisionMessage, setDivisionMessage] = useState('')
  const [editingDivisionId, setEditingDivisionId] = useState<string | null>(null)
  const [divisionForm, setDivisionForm] = useState({ name: '', code: '', ownerUserId: '' })
  const [subdivisionMessage, setSubdivisionMessage] = useState('')
  const [editingSubdivisionId, setEditingSubdivisionId] = useState<string | null>(null)
  const [subdivisionForm, setSubdivisionForm] = useState({ divisionId: '', name: '', code: '', ownerUserId: '' })
  const [businessEntityMessage, setBusinessEntityMessage] = useState('')
  const [editingBusinessEntityId, setEditingBusinessEntityId] = useState<string | null>(null)
  const [businessEntityForm, setBusinessEntityForm] = useState({
    subdivisionId: '',
    name: '',
    code: '',
    address: '',
    ownerUserId: '',
    marketingManagerUserId: '',
  })

  const load = () => {
    setError('')
    Promise.all([
      fetchUsers(),
      fetchDivisions(),
      fetchSubdivisions(),
      fetchBusinessEntities(),
    ])
      .then(([u, d, s, b]) => {
        setUsers(u)
        setDivisions(d)
        setSubdivisions(s)
        setBusinessEntities(b)
      })
      .catch((err) => setError(err.response?.data?.message ?? err.message ?? 'Failed to load'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (isAuthenticated) load()
  }, [isAuthenticated])

  const resetDivisionForm = () => {
    setEditingDivisionId(null)
    setDivisionForm({ name: '', code: '', ownerUserId: '' })
  }

  const handleEditDivision = (division: DivisionDto) => {
    setEditingDivisionId(division.id)
    setDivisionForm({
      name: division.name,
      code: division.code,
      ownerUserId: division.ownerUserId ?? '',
    })
    setDivisionMessage('')
  }

  const handleSaveDivision = async () => {
    if (!divisionForm.name.trim() || !divisionForm.code.trim()) {
      setError('Division name and code are required.')
      return
    }

    setError('')
    setDivisionMessage('')
    try {
      const payload = {
        name: divisionForm.name.trim(),
        code: divisionForm.code.trim(),
        ownerUserId: divisionForm.ownerUserId.trim() || undefined,
      }
      if (editingDivisionId) {
        await updateDivision(editingDivisionId, payload)
        setDivisionMessage('Division updated.')
      } else {
        await createDivision(payload)
        setDivisionMessage('Division added.')
      }
      resetDivisionForm()
      load()
    } catch (err: any) {
      setError(err.response?.data?.message ?? err.response?.data ?? err.message ?? 'Division save failed.')
    }
  }

  const handleDeleteDivision = async (division: DivisionDto) => {
    if (!confirm(`Delete division "${division.name}"? This only works when nothing is linked to it.`)) return
    setError('')
    setDivisionMessage('')
    try {
      await deleteDivision(division.id)
      if (editingDivisionId === division.id) resetDivisionForm()
      setDivisionMessage('Division removed.')
      load()
    } catch (err: any) {
      setError(err.response?.data?.message ?? err.response?.data ?? err.message ?? 'Division delete failed.')
    }
  }

  const resetSubdivisionForm = () => {
    setEditingSubdivisionId(null)
    setSubdivisionForm({ divisionId: '', name: '', code: '', ownerUserId: '' })
  }

  const handleEditSubdivision = (subdivision: SubdivisionDto) => {
    setEditingSubdivisionId(subdivision.id)
    setSubdivisionForm({
      divisionId: subdivision.divisionId,
      name: subdivision.name,
      code: subdivision.code,
      ownerUserId: subdivision.ownerUserId ?? '',
    })
    setSubdivisionMessage('')
  }

  const handleSaveSubdivision = async () => {
    if (!subdivisionForm.divisionId) {
      setError('Choose a parent division.')
      return
    }
    if (!subdivisionForm.name.trim() || !subdivisionForm.code.trim()) {
      setError('Subdivision name and code are required.')
      return
    }

    setError('')
    setSubdivisionMessage('')
    try {
      const payload = {
        divisionId: subdivisionForm.divisionId,
        name: subdivisionForm.name.trim(),
        code: subdivisionForm.code.trim(),
        ownerUserId: subdivisionForm.ownerUserId.trim() || undefined,
      }
      if (editingSubdivisionId) {
        await updateSubdivision(editingSubdivisionId, payload)
        setSubdivisionMessage('Subdivision updated.')
      } else {
        await createSubdivision(payload)
        setSubdivisionMessage('Subdivision added.')
      }
      resetSubdivisionForm()
      load()
    } catch (err: any) {
      setError(err.response?.data?.message ?? err.response?.data ?? err.message ?? 'Subdivision save failed.')
    }
  }

  const resetBusinessEntityForm = () => {
    setEditingBusinessEntityId(null)
    setBusinessEntityForm({ subdivisionId: '', name: '', code: '', address: '', ownerUserId: '', marketingManagerUserId: '' })
  }

  const handleEditBusinessEntity = (entity: BusinessEntityDto) => {
    setEditingBusinessEntityId(entity.id)
    setBusinessEntityForm({
      subdivisionId: entity.subdivisionId,
      name: entity.name,
      code: entity.code,
      address: entity.address ?? '',
      ownerUserId: entity.ownerUserId ?? '',
      marketingManagerUserId: entity.marketingManagerUserId ?? '',
    })
    setBusinessEntityMessage('')
  }

  const handleSaveBusinessEntity = async () => {
    if (!businessEntityForm.subdivisionId) {
      setError('Choose a parent subdivision.')
      return
    }
    if (!businessEntityForm.name.trim() || !businessEntityForm.code.trim()) {
      setError('Business entity name and code are required.')
      return
    }

    setError('')
    setBusinessEntityMessage('')
    try {
      const payload = {
        subdivisionId: businessEntityForm.subdivisionId,
        name: businessEntityForm.name.trim(),
        code: businessEntityForm.code.trim(),
        address: businessEntityForm.address.trim() || undefined,
        ownerUserId: businessEntityForm.ownerUserId.trim() || undefined,
        marketingManagerUserId: businessEntityForm.marketingManagerUserId.trim() || undefined,
      }
      if (editingBusinessEntityId) {
        await updateBusinessEntity(editingBusinessEntityId, payload)
        setBusinessEntityMessage('Business entity updated.')
      } else {
        await createBusinessEntity(payload)
        setBusinessEntityMessage('Business entity added.')
      }
      resetBusinessEntityForm()
      load()
    } catch (err: any) {
      setError(err.response?.data?.message ?? err.response?.data ?? err.message ?? 'Business entity save failed.')
    }
  }

  const handleDeleteBusinessEntity = async (entity: BusinessEntityDto) => {
    if (!confirm(`Delete business entity "${entity.name}"? This only works when nothing is linked to it.`)) return
    setError('')
    setBusinessEntityMessage('')
    try {
      await deleteBusinessEntity(entity.id)
      if (editingBusinessEntityId === entity.id) resetBusinessEntityForm()
      setBusinessEntityMessage('Business entity removed.')
      load()
    } catch (err: any) {
      setError(err.response?.data?.message ?? err.response?.data ?? err.message ?? 'Business entity delete failed.')
    }
  }

  if (!isAuthenticated) {
    return (
      <Container className="py-3">
        <Alert variant="info" className="mb-0">
          Sign in to manage divisions.
        </Alert>
      </Container>
    )
  }

  if (currentUser?.businessLevel !== 'MasterAdmin') {
    return (
      <Container className="py-3">
        <Alert variant="warning" className="mb-0">
          Division management is available to administrators only.
        </Alert>
      </Container>
    )
  }

  return (
    <Container className="py-3">
      <div className="mb-3">
        <h1 className="h3 mb-1">Division management</h1>
        <p className="text-muted mb-0">Manage the organization hierarchy: divisions, subdivisions, and the business entities that products, PCR data, and website checks are scoped to.</p>
      </div>

      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      {loading && <div className="text-muted mb-3">Loading organization data...</div>}

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h2 className="h6 mb-1">Division administration</h2>
              <p className="text-muted mb-0">Add or remove organization divisions used for user scope and product assignment.</p>
            </div>
            {editingDivisionId && (
              <Button type="button" variant="outline-secondary" size="sm" onClick={resetDivisionForm}>
                New division
              </Button>
            )}
          </div>
          {divisionMessage && <Alert variant="success" className="py-2">{divisionMessage}</Alert>}
          <Form className="row g-2 align-items-end mb-3">
            <Form.Group className="col-md-4">
              <Form.Label>Division name</Form.Label>
              <Form.Control
                value={divisionForm.name}
                onChange={(e) => setDivisionForm({ ...divisionForm, name: e.target.value })}
                placeholder="e.g. Insulation"
              />
            </Form.Group>
            <Form.Group className="col-md-2">
              <Form.Label>Code</Form.Label>
              <Form.Control
                value={divisionForm.code}
                onChange={(e) => setDivisionForm({ ...divisionForm, code: e.target.value })}
                placeholder="INS"
              />
            </Form.Group>
            <Form.Group className="col-md-4">
              <Form.Label>Administrator</Form.Label>
              <Form.Select
                value={divisionForm.ownerUserId}
                onChange={(e) => setDivisionForm({ ...divisionForm, ownerUserId: e.target.value })}
              >
                <option value="">No assigned administrator</option>
                {users.map((u) => (
                  <option key={u.id} value={u.userId}>{u.displayName || u.email || u.userId}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-2 d-grid">
              <Button type="button" variant="outline-primary" onClick={handleSaveDivision}>
                {editingDivisionId ? 'Save division' : 'Add division'}
              </Button>
            </Form.Group>
          </Form>
          <Table size="sm" responsive hover className="mb-0">
            <thead>
              <tr>
                <th>Division</th>
                <th>Code</th>
                <th>Administrator</th>
                <th style={{ width: 150 }}></th>
              </tr>
            </thead>
            <tbody>
              {divisions.map((division) => (
                <tr key={division.id}>
                  <td>{division.name}</td>
                  <td>{division.code}</td>
                  <td>{division.ownerUserId ? users.find((u) => u.userId === division.ownerUserId)?.displayName ?? division.ownerUserId : '-'}</td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => handleEditDivision(division)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDeleteDivision(division)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h2 className="h6 mb-1">Subdivision administration</h2>
              <p className="text-muted mb-0">Subdivisions belong to a division and group the business entities beneath them.</p>
            </div>
            {editingSubdivisionId && (
              <Button type="button" variant="outline-secondary" size="sm" onClick={resetSubdivisionForm}>
                New subdivision
              </Button>
            )}
          </div>
          {subdivisionMessage && <Alert variant="success" className="py-2">{subdivisionMessage}</Alert>}
          <Form className="row g-2 align-items-end mb-3">
            <Form.Group className="col-md-3">
              <Form.Label>Division *</Form.Label>
              <Form.Select
                value={subdivisionForm.divisionId}
                onChange={(e) => setSubdivisionForm({ ...subdivisionForm, divisionId: e.target.value })}
              >
                <option value="">Select division</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-3">
              <Form.Label>Subdivision name</Form.Label>
              <Form.Control
                value={subdivisionForm.name}
                onChange={(e) => setSubdivisionForm({ ...subdivisionForm, name: e.target.value })}
                placeholder="e.g. Insulated Panels EAA"
              />
            </Form.Group>
            <Form.Group className="col-md-2">
              <Form.Label>Code</Form.Label>
              <Form.Control
                value={subdivisionForm.code}
                onChange={(e) => setSubdivisionForm({ ...subdivisionForm, code: e.target.value })}
                placeholder="IPN-EAA"
              />
            </Form.Group>
            <Form.Group className="col-md-3">
              <Form.Label>Administrator</Form.Label>
              <Form.Select
                value={subdivisionForm.ownerUserId}
                onChange={(e) => setSubdivisionForm({ ...subdivisionForm, ownerUserId: e.target.value })}
              >
                <option value="">No assigned administrator</option>
                {users.map((u) => (
                  <option key={u.id} value={u.userId}>{u.displayName || u.email || u.userId}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-1 d-grid">
              <Button type="button" variant="outline-primary" onClick={handleSaveSubdivision}>
                {editingSubdivisionId ? 'Save' : 'Add'}
              </Button>
            </Form.Group>
          </Form>
          <Table size="sm" responsive hover className="mb-0">
            <thead>
              <tr>
                <th>Subdivision</th>
                <th>Code</th>
                <th>Division</th>
                <th>Administrator</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {subdivisions.map((subdivision) => (
                <tr key={subdivision.id}>
                  <td>{subdivision.name}</td>
                  <td>{subdivision.code}</td>
                  <td>{subdivision.divisionName ?? divisions.find((d) => d.id === subdivision.divisionId)?.name ?? '-'}</td>
                  <td>{subdivision.ownerUserId ? users.find((u) => u.userId === subdivision.ownerUserId)?.displayName ?? subdivision.ownerUserId : '-'}</td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-secondary" onClick={() => handleEditSubdivision(subdivision)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
            <div>
              <h2 className="h6 mb-1">Business entity administration</h2>
              <p className="text-muted mb-0">Business entities belong to a subdivision and are the level products, PCR data and website checks are scoped to.</p>
            </div>
            {editingBusinessEntityId && (
              <Button type="button" variant="outline-secondary" size="sm" onClick={resetBusinessEntityForm}>
                New business entity
              </Button>
            )}
          </div>
          {businessEntityMessage && <Alert variant="success" className="py-2">{businessEntityMessage}</Alert>}
          <Form className="row g-2 align-items-end mb-3">
            <Form.Group className="col-md-3">
              <Form.Label>Subdivision *</Form.Label>
              <Form.Select
                value={businessEntityForm.subdivisionId}
                onChange={(e) => setBusinessEntityForm({ ...businessEntityForm, subdivisionId: e.target.value })}
              >
                <option value="">Select subdivision</option>
                {subdivisions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.divisionName ?? divisions.find((d) => d.id === s.divisionId)?.name ?? 'Division'})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-2">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={businessEntityForm.name}
                onChange={(e) => setBusinessEntityForm({ ...businessEntityForm, name: e.target.value })}
                placeholder="e.g. Kingspan Insulation GB & I"
              />
            </Form.Group>
            <Form.Group className="col-md-2">
              <Form.Label>Code</Form.Label>
              <Form.Control
                value={businessEntityForm.code}
                onChange={(e) => setBusinessEntityForm({ ...businessEntityForm, code: e.target.value })}
                placeholder="INS-GBI"
              />
            </Form.Group>
            <Form.Group className="col-md-2">
              <Form.Label>Owner</Form.Label>
              <Form.Select
                value={businessEntityForm.ownerUserId}
                onChange={(e) => setBusinessEntityForm({ ...businessEntityForm, ownerUserId: e.target.value })}
              >
                <option value="">None</option>
                {users.map((u) => (
                  <option key={u.id} value={u.userId}>{u.displayName || u.email || u.userId}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-2">
              <Form.Label>Marketing manager</Form.Label>
              <Form.Select
                value={businessEntityForm.marketingManagerUserId}
                onChange={(e) => setBusinessEntityForm({ ...businessEntityForm, marketingManagerUserId: e.target.value })}
              >
                <option value="">None</option>
                {users.map((u) => (
                  <option key={u.id} value={u.userId}>{u.displayName || u.email || u.userId}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-1 d-grid">
              <Button type="button" variant="outline-primary" onClick={handleSaveBusinessEntity}>
                {editingBusinessEntityId ? 'Save' : 'Add'}
              </Button>
            </Form.Group>
            <Form.Group className="col-md-6">
              <Form.Label>Address</Form.Label>
              <Form.Control
                value={businessEntityForm.address}
                onChange={(e) => setBusinessEntityForm({ ...businessEntityForm, address: e.target.value })}
                placeholder="Optional"
              />
            </Form.Group>
          </Form>
          <Table size="sm" responsive hover className="mb-0">
            <thead>
              <tr>
                <th>Business entity</th>
                <th>Code</th>
                <th>Subdivision</th>
                <th>Division</th>
                <th>Owner</th>
                <th style={{ width: 150 }}></th>
              </tr>
            </thead>
            <tbody>
              {businessEntities.map((entity) => (
                <tr key={entity.id}>
                  <td>{entity.name}</td>
                  <td>{entity.code}</td>
                  <td>{entity.subdivisionName ?? subdivisions.find((s) => s.id === entity.subdivisionId)?.name ?? '-'}</td>
                  <td>{entity.divisionName ?? '-'}</td>
                  <td>{entity.ownerUserId ? users.find((u) => u.userId === entity.ownerUserId)?.displayName ?? entity.ownerUserId : '-'}</td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => handleEditBusinessEntity(entity)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDeleteBusinessEntity(entity)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  )
}
